import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import SafeHtml from "@/components/base/SafeHtml";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Contact", item: "https://cashmyproperty.ai/contact" },
  ],
};

export default function ContactPage() {
  usePageMeta({
    title: "Contact Us — Talk to a Land Strategist | CashMyProperty.ai",
    description: "Contact CashMyProperty.ai to unlock the hidden value in your land. Get a free AI feasibility report, speak to a land strategist, or list your property — we respond within 24 hours.",
    canonicalUrl: "https://cashmyproperty.ai/contact",
  });
  const { getContent } = usePageContent('contact');
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const contactEmail = settings.general.contact_email || 'support@cashmyproperty.ai';
  const address = settings.general.address || 'Bengaluru, Karnataka, India';

  const contactInfo = [
    { icon: "ri-phone-line", label: "Phone", value: displayPhone, href: `tel:${phoneRaw}` },
    { icon: "ri-mail-line", label: "Email", value: contactEmail, href: `mailto:${contactEmail}` },
    { icon: "ri-map-pin-line", label: "Office", value: address, href: "#" },
    { icon: "ri-time-line", label: "Response Time", value: "Within 24 hours", href: "#" },
  ];

  const sectionHeading = getContent('contact-section-heading') || 'Talk to a Land Strategist';
  const sectionSub = getContent('contact-section-sub');

  const { status, errorMsg, charCount, setCharCount, submit } = useFormSubmit({
    formType: "contact",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <PageHero
        badge="Get In Touch"
        title="Unlock the Hidden Gold"
        highlight="in Your Dirt"
        subtitle="One conversation could change what your land is worth forever."
        image="https://readdy.ai/api/search-image?query=Warm%20professional%20office%20consultation%20between%20real%20estate%20consultant%20and%20client%2C%20documents%20and%20maps%20on%20table%2C%20dark%20moody%20office%20environment%2C%20teal%20and%20amber%20accent%20lighting%2C%20cinematic%20business%20photography%2C%20India%20professional%20setting%2C%20trust%20and%20confidence%20mood&width=1800&height=800&seq=contact-hero-page&orientation=landscape"
      />

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Contact</span>
          </nav>
        </div>
      </section>

      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">

            {/* Left: info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-3">
                {sectionHeading}
              </h2>
              <p className="text-foreground-600 mb-8 leading-relaxed">
                {sectionSub && sectionSub.includes('<p>') ? (
                  <SafeHtml html={sectionSub} tag="span" />
                ) : (
                  sectionSub || 'Don\'t let your biggest asset sleep. Get a free preliminary assessment today. Our team will call you within 24 hours and provide initial guidance — completely free.'
                )}
              </p>

              <div className="space-y-4 mb-10">
                {contactInfo.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    className="flex items-center gap-4 p-4 rounded-xl card-dark hover:border-primary-500/40 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors shrink-0">
                      <i className={`${c.icon} text-xl text-primary-500`} />
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{c.label}</div>
                      <div className="text-sm text-foreground-950 font-medium mt-0.5">{c.value}</div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Testimonial */}
              <div className="p-6 rounded-2xl card-dark">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-accent-500 text-sm" />)}
                </div>
                <p className="text-sm text-foreground-600 italic leading-relaxed">
                  &ldquo;CashMyProperty.ai unlocked ₹2.5 Lakh monthly income from my highway plot that was sitting idle
                  for 12 years. The team handled everything — I didn&apos;t have to lift a finger.&rdquo;
                </p>
                <div className="mt-3 text-sm text-foreground-500 font-medium">
                  — Rajesh S., NRI Landowner, Dubai
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div>
              <form onSubmit={submit} className="p-8 rounded-2xl card-dark space-y-5">
                <div>
                  <h3 className="font-display font-semibold text-xl text-foreground-950 mb-1">
                    Get Your Feasible AI Report
                  </h3>
                  <p className="text-sm text-foreground-500">Fill in the details and we&apos;ll call you within 24 hours.</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    name="name"
                    placeholder="Full Name *"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                  />
                  <input
                    name="email"
                    type="email"
                    placeholder="Email Address *"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                  />
                </div>
                <input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number *"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                />
                <input
                  name="location"
                  placeholder="Land Location (City / Area) *"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <select
                    name="landSize"
                    className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                  >
                    <option value="">Land Size (Approx)</option>
                    <option value="small">Less than 5,000 sq.ft</option>
                    <option value="medium">5,000 – 10,000 sq.ft</option>
                    <option value="large">10,000 sq.ft – 1 Acre</option>
                    <option value="xlarge">1 Acre+</option>
                  </select>
                  <select
                    name="interest"
                    className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50"
                  >
                    <option value="">I&apos;m interested in...</option>
                    <option value="jda">Joint Development (JDA)</option>
                    <option value="lease">Long-term Lease</option>
                    <option value="retail">Retail / Franchise</option>
                    <option value="equity">Land-as-Equity</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                </div>
                <div className="relative">
                  <textarea
                    name="message"
                    placeholder="Tell us about your land (optional) — location details, current use, any specific goals..."
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl bg-background-300/50 border border-background-400/30 text-foreground-950 placeholder:text-foreground-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700/50 resize-none"
                  />
                  <span className={`absolute bottom-2 right-3 text-xs ${charCount > 450 ? "text-accent-500" : "text-foreground-500"}`}>
                    {charCount}/500
                  </span>
                </div>

                {/* Hidden source */}
                <input type="hidden" name="source" value="Contact Page" />

                {status === "success" && (
                  <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-700/30 text-primary-600 text-sm text-center">
                    <i className="ri-checkbox-circle-line mr-2" />
                    Thank you! We&apos;ll call you within 24 hours.
                  </div>
                )}
                {status === "error" && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-800/30 text-red-400 text-sm text-center">
                    <i className="ri-error-warning-line mr-2" />
                    {errorMsg || "Something went wrong. Please try again or call us directly."}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === "loading" ? "Submitting..." : "Get My Feasible AI Report"}
                </button>
                <p className="text-xs text-foreground-500 text-center">
                  No spam. 100% confidential. We never share your data.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}